import type {
  BundeslandLocationsListEntry,
  BundeslandLocationsListResolved,
} from './types';
import { Button } from '@/components/Button';
import { Link } from '@/components/Link';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';

// Tailwind can't interpolate a runtime `columns-${n}` class, so the 3
// possible `columns` config values are mapped to their literal desktop
// class here. Mobile is always a single column.
const DESKTOP_COLUMNS_CLASS: Record<'1' | '2' | '3', string> = {
  '1': 'md:columns-1',
  '2': 'md:columns-2',
  '3': 'md:columns-3',
};

// Regular body-text link styling — no bold, no arrow icon (unlike
// components/LinkList.tsx, which is deliberately not reused here).
const LINK_CLASS = 'text-primary hover:underline';

function EntryItem({ entry }: { entry: BundeslandLocationsListEntry }) {
  if (entry.type === 'group') {
    return (
      <li className="break-inside-avoid">
        <Link href={entry.href} className={LINK_CLASS}>
          {entry.label}
        </Link>
        {/* `circle` mirrors the spec mockup's hollow "°" sub-marker, one
            tier below the filled disc bullet on the enclosing <ul>. */}
        <ul className="mt-1 ml-4 list-inside list-[circle] space-y-1">
          {entry.items.map((item, i) => (
            <li key={`${item.href}-${i}`}>
              <Link href={item.href} className={LINK_CLASS}>
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li>
      <Link href={entry.href} className={LINK_CLASS}>
        {entry.label}
      </Link>
    </li>
  );
}

export function BundeslandLocationsListComponent({
  resolved,
}: {
  resolved: BundeslandLocationsListResolved;
}) {
  const entries = resolved.entries ?? [];

  if (entries.length === 0) {
    return null;
  }

  const columnsClass = DESKTOP_COLUMNS_CLASS[resolved.columns ?? '3'];

  return (
    <nav aria-label="Standort finden" className="pt-4">
      <ul
        className={`columns-1 ${columnsClass} list-inside list-disc gap-8 space-y-1 [column-fill:balance]`}
      >
        {entries.map((entry, i) => (
          <EntryItem key={`${entry.type}-${entry.href}-${i}`} entry={entry} />
        ))}
      </ul>
      {resolved.trailingCta && (
        <div className="mt-6 text-center">
          <Button
            href={resolved.trailingCta.href}
            variant="primary"
            onClick={() =>
              pushCmsEventTracking(resolved.trailingCtaTracking, {
                label: resolved.trailingCta?.label,
                pageUrl:
                  typeof window !== 'undefined' ? window.location.href : '',
              })
            }
          >
            {resolved.trailingCta.label}
          </Button>
        </div>
      )}
    </nav>
  );
}
